'use client';
/**
 * LA FENÊTRE D'UNE JOURNÉE — le cadre, partagé (RQ, 05/10/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Quand je suis sur un calendrier mensuel, quand je clique sur une journée,
 * je veux la voir en mode jour. »
 *
 * ── Un CADRE commun, un CONTENU propre à chaque écran ─────────────────────
 *
 * La journée ne dit pas la même chose selon l'écran qui l'ouvre : dans « Mon
 * planning » c'est MA journée (mes postes, mon absence) ; dans le planning du
 * laboratoire c'est la journée de TOUT LE MONDE (qui tient quoi, et ce que
 * personne ne tient). Fondre les deux aurait donné une fenêtre qui montre à
 * chacun ce qui ne le concerne pas, ou qui cache à la RH ce qu'elle cherche.
 *
 * Ce composant ne porte donc QUE ce qui doit être identique d'un écran à
 * l'autre : la date en toutes lettres, le déplacement jour par jour, le repère
 * de jour férié, et la façon de fermer. Le corps est passé en `children`.
 *
 * ── Pourquoi une FENÊTRE et non une vue ──────────────────────────────────
 *
 * Arbitrage de la RQ : « une fenêtre sur le calendrier ». On garde le mois
 * derrière — on ouvre trois jours de suite sans perdre sa place, ce qu'une
 * navigation aurait coûté à chaque aller-retour.
 *
 * ⚠️ `sansSaisie` sur la `Modale` : c'est une fenêtre de CONSULTATION, la croix
 * ferme sans rien demander. Sans elle, le moindre `<select>` de filtre dans le
 * corps déclencherait l'alerte « vous avez une saisie en cours » — et une
 * alerte qui se trompe est une alerte qu'on cesse de lire.
 *
 * ⚠️ L'AGENDA DU TABLEAU DE BORD N'EMPLOIE PAS CETTE FENÊTRE, et c'est
 * volontaire : il a DÉJÀ un mode jour (`vue === 'jour'`), éprouvé et plus riche
 * que ce cadre. Le clic sur une date y bascule donc sur sa propre vue. Un
 * second affichage de la même journée aurait divergé du premier.
 */
import React from 'react';
import { Modale, I } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { libelleJourLong, jourPlus, finDeSemaine } from '@/lib/jour';
import { COULEUR_FERIE, libelleCourtFerie, titreFerie, type FerieVue } from '@/lib/feries';
import { PointsConventions } from '@/components/BandeauConventions';
import type { OccurrenceConvention } from '@/lib/conventions';
import { aujourdhuiIso } from '@/lib/meta';

export function FenetreJour({
  date, onDate, onClose, ferie, conventions, children, largeur = 620, eyebrow,
}: {
  /** Le jour affiché, `AAAA-MM-JJ`. */
  date: string;
  /** Aller à un autre jour. Absent = pas de flèches (journée figée). */
  onDate?: (iso: string) => void;
  onClose: () => void;
  /** Le férié de CE jour, s'il y en a un. */
  ferie?: FerieVue | null;
  /** Les conventions de CE jour — mêmes points que dans la grille. */
  conventions?: OccurrenceConvention[];
  children: React.ReactNode;
  largeur?: number;
  /** D'où l'on vient (« Mon planning »), pour que la fenêtre se situe. */
  eyebrow?: string;
}) {
  /* Le jour de la semaine est DANS le titre (« lundi 5 octobre 2026 ») : une
     date seule oblige à compter sur ses doigts pour savoir si c'est un samedi,
     et le laboratoire n'ouvre pas pareil selon le jour. */
  const titre = libelleJourLong(date) || date;

  return (
    <Modale titre={titre} largeur={largeur} onClose={onClose} sansSaisie
      sousTitre={(
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {eyebrow && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{eyebrow}</span>}
          {/* ⚠️ « Samedi / dimanche » est un REPÈRE, pas une fermeture : le
              laboratoire ouvre le samedi et travaille les dimanches. Le mot
              « fermé » n'apparaît nulle part ici. */}
          {finDeSemaine(date) && (
            <span style={{ fontSize: 11.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--gray-25)', color: 'var(--text-muted)' }}>
              Fin de semaine
            </span>
          )}
          {ferie && (
            <span title={titreFerie(ferie)} aria-label={titreFerie(ferie)}
              style={{ fontSize: 11.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: COULEUR_FERIE.fond, color: COULEUR_FERIE.texte, border: `1px solid ${COULEUR_FERIE.bord}` }}>
              {libelleCourtFerie(ferie.libelle)}
            </span>
          )}
          <PointsConventions liste={conventions} />
        </span>
      )}>
      {/* Le déplacement jour par jour : on vient voir « et demain ? » tout de
          suite après. Sans ces flèches, il faudrait fermer, viser une autre
          case, rouvrir. */}
      {onDate && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Bouton taille="sm" aria-label="Jour précédent" onClick={() => onDate(jourPlus(date, -1))}>
            <I n="chevron-left" s={13} />
          </Bouton>
          <Bouton taille="sm" aria-label="Jour suivant" onClick={() => onDate(jourPlus(date, 1))}>
            <I n="chevron-right" s={13} />
          </Bouton>
          <Bouton taille="sm" onClick={() => onDate(aujourdhuiIso())}>
            Aujourd’hui
          </Bouton>
        </div>
      )}
      {children}
    </Modale>
  );
}

/** Un intitulé de rubrique dans la fenêtre — pour que les écrans n'inventent
 *  pas trois typographies pour la même chose. */
export function RubriqueJour({ titre, children }: { titre: string; children: React.ReactNode }) {
  return (
    <section style={{ display: 'grid', gap: 6, marginBottom: 14 }}>
      <h3 style={{ margin: 0, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
        {titre}
      </h3>
      {children}
    </section>
  );
}
